import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { errorMessage, post, put } from "@/lib/api";
import { fetchLocksmiths, fetchOfficers, fetchRun, useAsync } from "@/lib/queries";
import { ENGINEER_JOB_TYPES, TEAM_ROLE_LABELS, WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { RUN_STATUSES, START_TIMES, formatTime, isValidPostcodeOrArea, normalizePostcode, statusAllowed, todayISO } from "@/lib/constants";
import { ErrorState, FieldError, LoadingRows, PageHeader } from "@/components/States";
import { AddressRowsEditor, newAddressRow } from "./AddressRowsEditor";
import { cleanMeterDetails, hasIncompleteMeterDetails, hasMeterDetails } from "@/components/MeterDetailsFields";
import { OfficerDialog } from "./OfficerDialog";
import { LocksmithDialog } from "./LocksmithDialog";
import { InviteLinkDialog } from "./InviteLinkDialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULT_WORKER_TYPES = WORKER_ROLE_OPTIONS.map(([role]) => role);
const EMPTY = { run_date: todayISO(), start_time: "08:00:00", locksmith_id: "", engineer_id: "", officer_id: "", status: "scheduled", notes: "", warrant_types: [], require_officer_signature: false, require_locksmith_signature: false, stab_vest_required: false };

async function loadForm(id) {
  const [locksmiths, officers, run] = await Promise.all([fetchLocksmiths(), fetchOfficers(), id ? fetchRun(id) : null]);
  return { locksmiths, officers, run };
}

function validate(run, rows, enabledTypes, requireLocksmith) {
  const e = { rows: [] };
  if (!run.run_date) e.run_date = "Choose a date";
  if (!run.start_time) e.start_time = "Choose a start time";
  if (requireLocksmith && !run.locksmith_id) e.locksmith_id = "Assign a locksmith";
  if (!run.officer_id) e.officer_id = "Select an officer";
  if (enabledTypes.length > 1 && !run.warrant_types.length) e.warrant_types = "Select at least one warrant type";
  rows.forEach((r, i) => {
    if (isBlankRow(r, run.warrant_types)) return;
    const re = {};
    if (!r.postcode.trim()) re.postcode = "Required";
    else if (!isValidPostcodeOrArea(r.postcode)) re.postcode = "Enter a valid UK postcode or the first part (e.g. E13)";
    if (r.status !== r.orig_status && !statusAllowed(run.run_date, r.status)) re.status = true;
    if (run.warrant_types.includes("engineer") && hasIncompleteMeterDetails(r.meter_details)) re.meter_details = "Each meter needs a type and serial";
    if (Object.keys(re).length) e.rows[i] = re;
  });
  const ok = Object.keys(e).length === 1 && !e.rows.some(Boolean);
  return { ok, e };
}

const isBlankRow = (r, warrantTypes) => !r.id && !r.has_shutter && ![
  r.postcode, r.full_address, r.instructions, r.notes, r.agent_notes, r.dog_handler_notes, r.shutter_details,
  r.customer_name, r.customer_telephone, r.debt_amount, r.vulnerability_text, r.dog_handler_id,
  ...(warrantTypes.includes("engineer") ? [r.engineer_notes, r.engineer_type, r.engineer_id] : []),
].some((v) => String(v).trim()) && !(warrantTypes.includes("engineer") && hasMeterDetails(r.meter_details)) && !String(r.previous_visits).trim();

const Field = ({ label, error, testId, children }) => (
  <div>
    <Label>{label}</Label>
    <div className="mt-1.5">{children}</div>
    <FieldError message={error} testId={testId} />
  </div>
);

const OPEN = { id: "open", full_name: "Open job: any locksmith can accept", active: true };

const PersonSelect = ({ value, onChange, items, placeholder, testId, currentId, disabled = false }) => (
  <Select value={value} onValueChange={(v) => v && onChange(v)} disabled={disabled}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {items.filter((x) => x.active || x.id === currentId).map((x) => (
        <SelectItem key={x.id} value={x.id}>{x.full_name || x.email}{x.company ? ` · ${x.company}` : ""}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function RunForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload, setData } = useAsync(() => loadForm(id), [id]);
  const { profile } = useAuth();
  const enabledTypes = profile?.company?.enabled_user_types || DEFAULT_WORKER_TYPES;
  const engineerJobTypes = profile?.company?.engineer_job_types ?? ENGINEER_JOB_TYPES;
  const basic = profile?.company?.basic_plan === true;
  const legacyRun = Boolean(id && data?.run && !Array.isArray(data.run.warrant_types));
  const assigned = data?.run?.locksmith;
  const hasUserType = (member, type) => (member.user_types || [member.role]).includes(type);
  const locksmithMembers = (data?.locksmiths || []).filter((member) => hasUserType(member, "locksmith") && member.membership_status === "active");
  const others = locksmithMembers.filter((l) => l.id !== profile.id)
    .map((l) => ({ ...l, full_name: `${l.full_name || l.email} (${TEAM_ROLE_LABELS[l.role] || l.role})` }));
  const assignable = [
    ...(basic ? [] : [OPEN]),
    ...((hasUserType(profile, "locksmith") || basic) ? [{ ...profile, company: undefined, active: true, full_name: `Me (${profile.full_name || profile.email})` }] : []),
    ...(assigned && assigned.id !== profile.id && !others.some((l) => l.id === assigned.id) ? [{ ...assigned, active: true }] : []),
    ...others,
  ];
  const engineers = (data?.locksmiths || []).filter((member) => (member.user_types || [member.role]).includes("engineer") && member.membership_status === "active");
  const dogHandlers = (data?.locksmiths || []).filter((member) => (member.user_types || [member.role]).includes("dog_handler") && member.membership_status === "active");
  const assignedEngineer = data?.run?.engineer;
  const engineerOptions = [
    { id: "unassigned", full_name: "Assign per address", active: true },
    ...engineers,
    ...(assignedEngineer && !engineers.some((member) => member.id === assignedEngineer.id) ? [{ ...assignedEngineer, active: true }] : []),
  ];
  const [run, setRun] = useState(() => ({ ...EMPTY, warrant_types: enabledTypes }));
  const showLocksmithField = run.warrant_types.includes("locksmith") || legacyRun;
  const showEngineerField = run.warrant_types.includes("engineer");
  const [rows, setRows] = useState([newAddressRow()]);
  const [errors, setErrors] = useState({ rows: [] });
  const [saving, setSaving] = useState(false);
  const [addingOfficer, setAddingOfficer] = useState(false);
  const [addingLocksmith, setAddingLocksmith] = useState(false);
  const [addingEngineer, setAddingEngineer] = useState(false);
  const [invite, setInvite] = useState(null);
  const officerOptions = [
    ...(data?.officers || []),
    ...(run.warrant_types.includes("agent") ? (data?.locksmiths || [])
      .filter((member) => hasUserType(member, "agent") && member.membership_status === "active")
      .map((member) => ({ ...member, full_name: `${member.full_name || member.email} (Agent user)` })) : []),
  ];

  const onLocksmithInvited = ({ name, link, locksmith }) => {
    setData({ ...data, locksmiths: [...data.locksmiths, locksmith].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "")) });
    setRun((r) => ({ ...r, locksmith_id: locksmith.id }));
    setErrors((e) => ({ ...e, locksmith_id: undefined }));
    setInvite({ name, link });
  };

  const onEngineerInvited = ({ name, link, locksmith }) => {
    setData({ ...data, locksmiths: [...data.locksmiths, locksmith].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "")) });
    setRun((r) => ({ ...r, engineer_id: locksmith.id }));
    setRows((current) => current.map((address) => ({ ...address, engineer_id: "" })));
    setInvite({ name, link });
  };

  const onOfficerAdded = (saved) => {
    const { account_user: accountUser, account_invite_link: inviteLink, ...officer } = saved;
    setData({
      ...data,
      officers: [...data.officers, officer].sort((a, b) => a.full_name.localeCompare(b.full_name)),
      locksmiths: accountUser ? [...data.locksmiths, accountUser] : data.locksmiths,
    });
    setRun((r) => ({ ...r, officer_id: accountUser?.id || officer.id }));
    setErrors((e) => ({ ...e, officer_id: undefined }));
    if (inviteLink) setInvite({ name: accountUser?.full_name || officer.full_name, link: inviteLink });
  };

  useEffect(() => {
    if (data?.run) {
      const r = data.run;
      const legacyRun = !Array.isArray(r.warrant_types);
      const warrantTypes = legacyRun ? ["locksmith"] : r.warrant_types.filter((role) => enabledTypes.includes(role));
      const engineerId = warrantTypes.includes("engineer") ? r.engineer_id || "" : "";
      setRun({ run_date: r.run_date, start_time: r.start_time, locksmith_id: basic ? profile.id : legacyRun || warrantTypes.includes("locksmith") ? r.locksmith_id || "open" : "", engineer_id: engineerId, officer_id: r.officer_id, status: r.status, notes: r.notes || "", warrant_types: warrantTypes.length ? warrantTypes : enabledTypes, require_officer_signature: !!r.require_officer_signature, require_locksmith_signature: !!r.require_locksmith_signature, stab_vest_required: !!r.stab_vest_required });
      setRows(r.run_addresses.length ? r.run_addresses.map((address) => newAddressRow({ ...address, engineer_id: engineerId ? "" : address.engineer_id })) : [newAddressRow()]);
    }
  }, [data, basic, profile.id, enabledTypes]);

  useEffect(() => {
    if (basic && !id) setRun((r) => ({ ...r, locksmith_id: profile.id }));
  }, [basic, id, profile.id]);

  const set = (patch) => setRun((r) => ({ ...r, ...patch }));

  const save = async (ev) => {
    ev.preventDefault();
    const { ok, e } = validate(run, rows, enabledTypes, showLocksmithField);
    setErrors(e);
    if (!ok) return toast.error("Please fix the highlighted fields");
    setSaving(true);
    try {
      const addresses = rows.filter((r) => !isBlankRow(r, run.warrant_types)).map((r) => ({ id: r.id, postcode: normalizePostcode(r.postcode), full_address: r.full_address.trim(), meter_details: showEngineerField ? cleanMeterDetails(r.meter_details) : [], instructions: r.instructions.trim() || null, notes: r.notes.trim() || null, agent_notes: r.agent_notes.trim() || null, engineer_notes: showEngineerField ? r.engineer_notes.trim() || null : null, dog_handler_notes: r.dog_handler_notes.trim() || null, customer_name: r.customer_name.trim() || null, customer_telephone: r.customer_telephone.trim() || null, debt_amount: r.debt_amount.trim() || null, vulnerability_text: r.vulnerability_text.trim() || null, previous_visits: r.previous_visits === "" ? null : Number(r.previous_visits), engineer_type: showEngineerField ? r.engineer_type || null : null, engineer_id: showEngineerField && !run.engineer_id ? r.engineer_id || null : null, dog_handler_id: run.warrant_types.includes("dog_handler") ? r.dog_handler_id || null : null, status: r.status, has_shutter: r.has_shutter, shutter_details: r.has_shutter ? r.shutter_details.trim() || null : null }));
      const body = { ...run, locksmith_id: run.locksmith_id === "open" ? null : run.locksmith_id, engineer_id: showEngineerField ? run.engineer_id || null : null, notes: run.notes.trim() || null, status: id ? run.status : null, addresses };
      const saved = id ? await put(`/runs/${id}`, body) : await post("/runs", body);
      toast.success(id ? "Warrant run updated" : "Warrant run created");
      navigate(`/manager/runs/${saved.id}`);
    } catch (err) {
      const msg = errorMessage(err);
      if (err.response?.status === 409) setErrors({ ...e, locksmith_id: msg });
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <LoadingRows rows={6} testId="run-form-loading" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  return (
    <>
    <form onSubmit={save} noValidate className="space-y-6" data-testid="run-form">
      <Link to={id ? `/manager/runs/${id}` : "/manager/runs"} className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid="run-form-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
      <PageHeader eyebrow={id ? "Edit" : "Create"} title={id ? "Edit Warrant Run" : "New Warrant Run"} description={`One officer, ${showLocksmithField ? "one locksmith and " : ""}one start time per run.`} />
      <section className="grid gap-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm md:grid-cols-2 lg:grid-cols-4">
        <Field label="Date *" error={errors.run_date} testId="run-date-error">
          <DatePicker value={run.run_date} onChange={(v) => set({ run_date: v })} testId="run-date-input" />
        </Field>
        <Field label="Start time *" error={errors.start_time} testId="run-start-time-error">
          <Select value={run.start_time} onValueChange={(v) => set({ start_time: v })}>
            <SelectTrigger data-testid="run-start-time-select"><SelectValue /></SelectTrigger>
            <SelectContent>{START_TIMES.map((t) => <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        {enabledTypes.length > 1 && <div className="md:col-span-2 lg:col-span-4">
          <Label>Warrant Resources</Label>
          <div className="mt-1.5 grid gap-2 sm:grid-cols-2" role="group" aria-label="Warrant Resources" data-testid="run-warrant-types-group">
            {WORKER_ROLE_OPTIONS.filter(([role]) => enabledTypes.includes(role)).map(([role, label]) => (
              <label key={role} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3">
                <Checkbox checked={run.warrant_types.includes(role)} onCheckedChange={(checked) => {
                  const warrant_types = checked ? [...run.warrant_types, role] : run.warrant_types.filter((value) => value !== role);
                  const assignedAgent = role === "agent" && !checked && (data?.locksmiths || []).some((member) => member.id === run.officer_id && (member.user_types || [member.role]).includes("agent"));
                  const clearLocksmith = role === "locksmith" && !checked && !legacyRun;
                  const clearEngineer = role === "engineer" && !checked;
                  set({ warrant_types, ...(assignedAgent ? { officer_id: "" } : {}), ...(clearLocksmith ? { locksmith_id: "" } : {}), ...(clearEngineer ? { engineer_id: "" } : {}) });
                  if (clearEngineer) setRows((current) => current.map((address) => ({ ...address, engineer_id: "" })));
                }} data-testid={`run-warrant-type-${role}`} />
                <span className="text-sm font-medium text-slate-800">{label}</span>
              </label>
            ))}
          </div>
          <FieldError message={errors.warrant_types} testId="run-warrant-types-error" />
        </div>}
        <Field label={run.warrant_types.includes("agent") ? "Agent or Warrant Agent *" : "Warrant Agent *"} error={errors.officer_id} testId="run-officer-error">
          <PersonSelect value={run.officer_id} onChange={(v) => set({ officer_id: v })} items={officerOptions} placeholder={officerOptions.length ? "Select officer or Agent user" : "Add an officer first"} testId="run-officer-select" currentId={data.run?.officer_id} />
          <button type="button" onClick={() => setAddingOfficer(true)} className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid="run-quick-add-officer-button">
            <Plus className="h-3.5 w-3.5" /> New officer
          </button>
        </Field>
        {showEngineerField && <Field label="Engineer" testId="run-engineer-error">
          <PersonSelect value={run.engineer_id || "unassigned"} onChange={(value) => {
            const engineer_id = value === "unassigned" ? "" : value;
            set({ engineer_id });
            if (engineer_id) setRows((current) => current.map((address) => ({ ...address, engineer_id: "" })));
          }} items={engineerOptions} placeholder="Assign Engineer or choose per address" testId="run-engineer-select" currentId={run.engineer_id} />
          {enabledTypes.includes("engineer") && <button type="button" onClick={() => setAddingEngineer(true)} className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid="run-quick-invite-engineer-button">
            <Plus className="h-3.5 w-3.5" /> Add new engineer
          </button>}
        </Field>}
        {showLocksmithField && <Field label="Locksmith *" error={errors.locksmith_id} testId="run-locksmith-error">
          <PersonSelect value={run.locksmith_id} onChange={(v) => set({ locksmith_id: v })} items={assignable} placeholder="Assign locksmith" testId="run-locksmith-select" currentId={data.run?.locksmith_id} disabled={basic} />
          {basic ? (
            <p className="mt-1.5 text-xs text-slate-500" data-testid="run-locksmith-basic-note">On the Basic plan runs are assigned to you. <Link to="/manager/billing" className="font-medium text-blue-700 hover:underline">Change plan</Link> to assign other locksmiths.</p>
          ) : run.locksmith_id === "open" ? (
            <p className="mt-1.5 text-xs text-amber-800" data-testid="run-locksmith-open-note">Every active locksmith gets an email and sees it in Available jobs. The first to accept gets the run.</p>
          ) : (
          <button type="button" onClick={() => setAddingLocksmith(true)} className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid="run-quick-invite-locksmith-button">
            <Plus className="h-3.5 w-3.5" /> Invite new locksmith
          </button>
          )}
        </Field>}
        {id && (
          <Field label="Run status">
            <Select value={run.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger data-testid="run-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RUN_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <div className={id ? "md:col-span-2 lg:col-span-3" : "md:col-span-2 lg:col-span-4"}>
          <Field label="Run notes">
            <Textarea rows={2} value={run.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="run-notes-input" />
          </Field>
          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Checkbox checked={run.require_officer_signature} onCheckedChange={(v) => set({ require_officer_signature: !!v })} className="mt-0.5" data-testid="run-require-signature-checkbox" />
            <span>
              <span className="block text-sm font-semibold text-slate-900">Require Warrant Officer's Signature</span>
              <span className="block text-xs text-slate-600">Adds a signature box on the run page for the officer to sign once for the whole run.</span>
            </span>
          </label>
          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Checkbox checked={run.require_locksmith_signature} onCheckedChange={(v) => set({ require_locksmith_signature: !!v })} className="mt-0.5" data-testid="run-require-locksmith-signature-checkbox" />
            <span>
              <span className="block text-sm font-semibold text-slate-900">Require Locksmith's Signature</span>
              <span className="block text-xs text-slate-600">Adds a signature box on the run page for the locksmith to sign once for the whole run.</span>
            </span>
          </label>
          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Checkbox checked={run.stab_vest_required} onCheckedChange={(v) => set({ stab_vest_required: !!v })} className="mt-0.5" data-testid="run-stab-vest-checkbox" />
            <span>
              <span className="block text-sm font-semibold text-slate-900">Stab Vest Required</span>
              <span className="block text-xs text-slate-600">Shows a clear warning on the run page so the locksmith knows to wear one.</span>
            </span>
          </label>
        </div>
      </section>
      <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Property addresses</h2>
          <p className="text-sm text-slate-500">Visited in this order. No individual appointment times.</p>
        </div>
        <AddressRowsEditor rows={rows} setRows={setRows} errors={errors.rows} listError={errors.list} runDate={run.run_date} warrantTypes={run.warrant_types} engineers={engineers} dogHandlers={dogHandlers} runEngineerId={run.engineer_id} jobTypes={engineerJobTypes} />
      </section>
      <div className="sticky bottom-0 -mx-4 flex justify-end gap-2 border-t border-slate-200 bg-white/90 p-4 backdrop-blur sm:-mx-6 lg:-mx-8">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={saving} data-testid="run-form-cancel-button">Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="run-form-save-button">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{id ? "Save changes" : "Create run"}
        </Button>
      </div>
    </form>
    <OfficerDialog open={addingOfficer} onOpenChange={setAddingOfficer} officer={null} onSaved={onOfficerAdded} allowAccountCreation={run.warrant_types.includes("agent")} />
    <LocksmithDialog open={addingLocksmith} onOpenChange={setAddingLocksmith} locksmith={null} onSaved={() => {}} onInvited={onLocksmithInvited} />
    <LocksmithDialog open={addingEngineer} onOpenChange={setAddingEngineer} locksmith={null} initialRole="engineer" onSaved={() => {}} onInvited={onEngineerInvited} />
    <InviteLinkDialog invite={invite} onClose={() => setInvite(null)} />
    </>
  );
}
